/// <reference lib="webworker" />
import { jsPDF } from 'jspdf'
import autoTable, { RowInput } from 'jspdf-autotable'
import {
  PdfWorkerRequest,
  PdfWorkerResponse,
  dokaLogo,
  measurementUnitArticle,
  measurementUnitIcon,
  slabThicknessIcon,
  slabTopLevelIcon,
  wallHeightIcon,
} from './resultPdfWorkerData'
import { PlanType } from '../../generated/efp-api'

const MAX_IMAGE_WIDTH = 190
const MAX_IMAGE_HEIGHT = 237
const ORIENTATION = 'portrait'

function isPdfWorkerData(data: unknown): data is PdfWorkerRequest {
  return (data as PdfWorkerRequest).isPdfWorkerRequest
}

addEventListener('message', ({ data }) => {
  if (!isPdfWorkerData(data)) {
    throw new Error('PDF worker data does not have correct format')
  }

  const pdfDoc = generatePdf(data)

  const response: PdfWorkerResponse = {
    isPdfWorkerResponse: true,
    pdfBlob: pdfDoc.output('blob'),
  }
  postMessage(response)
})

/**
 * Generate a result pdf for the given data.
 *
 * This method is only exported so it can be tested!
 */
export function generatePdf(data: PdfWorkerRequest): jsPDF {
  // A4 210mm x 297mm
  const doc = new jsPDF({
    unit: 'mm',
    compress: true,
    orientation: ORIENTATION,
    format: 'a4',
  })
  // jsPDF creates a blank first page, remove it as we add pages with their headers as needed
  doc.deletePage(1)
  doc.setFontSize(8)

  if (data.EFPWebLink) {
    addPage(doc, data)
    doc.setFontSize(12)
    addHeadline(doc, data.translations['ACC.PDF_HEADER'])

    doc.setFontSize(8)
    doc.textWithLink(data.translations['ACC.PDF_MESSAGE'], 14, 35, { url: data.EFPWebLink })
  }

  if (data.logMessages.length > 0) {
    addPage(doc, data)

    const pageWidth = getPageWidth(doc)
    addHeadline(doc, data.translations['PDF.ERROR_CALCULATION'])
    doc.setFontSize(12)
    let yMultiplicator = 0
    data.logMessages.forEach((message) => {
      const splitText = doc.splitTextToSize(message, pageWidth - 30) as string[]
      splitText.forEach((text, index) => {
        // \u2022 = bullet point for ul
        doc.text((index === 0 ? '\u2022' + ' ' : '') + text, 14, 35 + yMultiplicator * 7)
        yMultiplicator++
      })
    })
    doc.setFontSize(8)
  }

  data.screens.forEach((screen) => {
    addPage(doc, data)

    addFullScreenImage(doc, screen.name, screen.screenshot, screen.width, screen.height)

    addMeasurementLegend(
      doc,
      data.buildingType,
      data.measurementUnit,
      data.slabThicknes,
      data.slabTopLevel,
      data.wallHeight,
      data.articleMeasurementUnit
    )

    if (screen.cycle !== 0 && data.includeCycleStock && screen.cycle !== 888) {
      addStock(doc, data, screen.cycle)
    }
  })

  if (data.includeTotalStock) {
    addStock(doc, data, 0)
  }

  return doc
}

function getPageWidth(doc: jsPDF): number {
  return doc.internal.pageSize.width || doc.internal.pageSize.getWidth()
}

function getPageHeight(doc: jsPDF): number {
  return doc.internal.pageSize.height || doc.internal.pageSize.getHeight()
}

function addPage(doc: jsPDF, data: PdfWorkerRequest): void {
  doc.addPage('a4', ORIENTATION)
  const pageWidth = getPageWidth(doc)
  const pageHeight = getPageHeight(doc)

  const pageCounter = doc.getNumberOfPages()
  doc.text(data.translations['PDF.PAGE'] + ' ' + pageCounter.toString(), pageWidth - 20, 10, {})
  doc.text(data.translations['PDF.CREATED_WITH'], 10, pageHeight - 9)
  doc.addImage(dokaLogo, 'PNG', 10, 7, 26.4, 8, 'logo', 'NONE', 0)
}

function addHeadline(doc: jsPDF, text: string): void {
  const pageWidth = getPageWidth(doc)
  doc.setFontSize(16)
  doc.text(text, pageWidth / 2, 23, { align: 'center' })
  doc.setFontSize(8)
}

function addStock(doc: jsPDF, data: PdfWorkerRequest, cycle: number): void {
  if (data.resultStock && data.resultStock.length > 0) {
    const row: RowInput[] = []
    addPage(doc, data)
    addHeadline(
      doc,
      cycle
        ? data.translations['VIEWER.CYCLE'] + ' ' + cycle
        : data.translations['PDF.TOTAL_AMOUNT']
    )

    data.resultStock.forEach((article) => {
      if (cycle !== 0 && article.cycleUsage[cycle - 1] > 0) {
        row.push([article.articleId, article.name, article.cycleUsage[cycle - 1].toString()])
      } else if (article.amount > 0) {
        row.push([article.articleId, article.name, article.amount.toString()])
      }
    })

    autoTable(doc, {
      columns: [
        { dataKey: 'ArticleNr', header: data.translations['PDF.ARTICLE_NR'] },
        {
          dataKey: 'Description',
          header: data.translations['PDF.DESCRIPTION'],
        },
        { dataKey: 'Amount', header: data.translations['PDF.AMOUNT'] },
      ],
      body: row,
      alternateRowStyles: { fillColor: [210, 210, 210], textColor: [0, 0, 0] },
      headStyles: { fillColor: [255, 255, 255], textColor: [0, 0, 0] },
      startY: 25,
    })
  }
}

function addFullScreenImage(
  doc: jsPDF,
  screenName: string,
  screenData: string,
  screenWidth: number,
  screenHeight: number
): void {
  addHeadline(doc, screenName)
  let width: number, height: number

  if (screenWidth < screenHeight) {
    height = MAX_IMAGE_HEIGHT
    width = height * (screenWidth / screenHeight)
    if (width > MAX_IMAGE_WIDTH) {
      // proportinal reduce the size of the height and set the width to MAX
      height = height - (width - MAX_IMAGE_WIDTH) * (screenWidth / screenHeight)
      width = MAX_IMAGE_WIDTH
    }
  } else if (screenWidth > screenHeight) {
    width = MAX_IMAGE_WIDTH
    height = width / (screenWidth / screenHeight)
    if (height > MAX_IMAGE_HEIGHT) {
      width = width - (height - MAX_IMAGE_HEIGHT) * (screenWidth / screenHeight)
      height = MAX_IMAGE_HEIGHT
    }
  } else {
    height = width = MAX_IMAGE_WIDTH
  }

  // 257, 170
  doc.addImage(
    screenData,
    'JPEG',
    (MAX_IMAGE_WIDTH - width + 20) / 2,
    (MAX_IMAGE_HEIGHT - height + 60) / 2,
    width,
    height,
    screenName,
    'MEDIUM',
    0
  )
}

function addMeasurementLegend(
  doc: jsPDF,
  buildingType: string,
  measurementUnit: string,
  slabThicknes: string,
  slabTopLevel: string,
  wallHeight: string,
  articleMeasurementUnit: string
): void {
  const pageWidth = getPageWidth(doc)
  const pageHeight = getPageHeight(doc)
  const showArticleMeasurementUnit =
    measurementUnit !== articleMeasurementUnit && buildingType === PlanType.Wall

  if (buildingType === 'WALL') {
    doc.addImage(
      wallHeightIcon,
      'PNG',
      showArticleMeasurementUnit ? pageWidth - 56 : pageWidth - 39,
      pageHeight - 13,
      6,
      6,
      'wallHeightIcon',
      'NONE',
      0
    )
    doc.text(
      wallHeight.toString(),
      showArticleMeasurementUnit ? pageWidth - 48 : pageWidth - 31,
      pageHeight - 9
    )
  } else {
    doc.addImage(
      slabThicknessIcon,
      'PNG',
      pageWidth - 56,
      pageHeight - 13,
      6,
      6,
      'slabThicknessIcon',
      'NONE',
      0
    )
    doc.text(slabThicknes.toString(), pageWidth - 47, pageHeight - 9)

    doc.addImage(
      slabTopLevelIcon,
      'PNG',
      pageWidth - 39,
      pageHeight - 13,
      6,
      6,
      'slabTopLevelIcon',
      'NONE',
      0
    )
    doc.text(slabTopLevel.toString(), pageWidth - 31, pageHeight - 9)
  }

  doc.addImage(
    measurementUnitIcon,
    'PNG',
    showArticleMeasurementUnit ? pageWidth - 39 : pageWidth - 22,
    pageHeight - 13,
    6,
    6,
    'measurementUnitIcon',
    'NONE',
    0
  )
  doc.text(
    measurementUnit,
    showArticleMeasurementUnit ? pageWidth - 31 : pageWidth - 15,
    pageHeight - 9
  )

  if (showArticleMeasurementUnit) {
    doc.addImage(
      measurementUnitArticle,
      'PNG',
      pageWidth - 22,
      pageHeight - 13,
      6,
      6,
      'measurementUnitArticle',
      'NONE',
      0
    )
    // always inch when needed
    doc.text(articleMeasurementUnit, pageWidth - 15, pageHeight - 9)
  }
}
